/**
 * IFRAP-AI.fr - Page Résumé IA
 * Génération de synthèses et configuration du scan automatique
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { 
  Sparkles, RefreshCw, Clock, Settings, Play, CheckCircle, 
  AlertTriangle, FileText, Calendar, Download, BookOpen
} from 'lucide-react';

const SummaryPage = () => {
  const [status, setStatus] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Report generation
  const [generatingReview, setGeneratingReview] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [pressReview, setPressReview] = useState(null);
  const [report, setReport] = useState(null);
  const [reportTopic, setReportTopic] = useState('');
  
  // Config form
  const [autoConfig, setAutoConfig] = useState({
    enabled: false,
    hour: 7,
    minute: 0,
    categories: ['think_tanks', 'media'],
    max_articles: 30,
  });
  
  // Scan options
  const [scanOptions, setScanOptions] = useState({
    categories: ['think_tanks', 'media'],
    max_articles: 20,
    force: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, scanRes, configRes] = await Promise.all([
        api.get('/api/summary/status'),
        api.get('/api/summary/last-scan').catch(() => ({ data: { has_scan: false } })),
        api.get('/api/summary/auto-scan/config').catch(() => ({ data: {} })),
      ]);
      
      setStatus(statusRes.data);
      setLastScan(scanRes.data);
      
      if (configRes.data) {
        const [hour, minute] = (configRes.data.time || '07:00').split(':').map(Number);
        setAutoConfig({
          enabled: configRes.data.enabled || false,
          hour: hour || 7,
          minute: minute || 0,
          categories: configRes.data.categories || ['think_tanks', 'media'],
          max_articles: configRes.data.max_articles || 30,
        });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setMessage(null);

    try {
      const res = await api.post('/api/summary/scan', scanOptions);
      setMessage({ 
        type: 'success', 
        text: `Scan terminé : ${res.data.summaries_generated}/${res.data.articles_processed} résumés générés` 
      });
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur lors du scan';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setScanning(false);
    }
  };

  const handleSaveAutoConfig = async () => {
    setMessage(null);
    try {
      await api.post('/api/summary/auto-scan/configure', autoConfig);
      setMessage({ type: 'success', text: 'Configuration sauvegardée' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    }
  };

  // Generate Press Review
  const handleGenerateReview = async () => {
    setGeneratingReview(true);
    setMessage(null);
    try {
      const res = await api.post('/api/content/press-review/generate', { max_articles: 10 });
      setPressReview(res.data.review || res.data.press_review);
      setMessage({ type: 'success', text: 'Revue de presse générée avec succès' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur génération revue de presse';
      setMessage({ type: 'error', text: msg });
    } finally {
      setGeneratingReview(false);
    }
  };

  // Generate Report
  const handleGenerateReport = async () => {
    if (!reportTopic.trim()) {
      setMessage({ type: 'error', text: 'Veuillez entrer un sujet pour le rapport' });
      return;
    }
    setGeneratingReport(true);
    setMessage(null);
    try {
      const res = await api.post('/api/content/report/generate', {
        topic: reportTopic,
        include_veille: true,
      });
      setReport(res.data.report);
      setMessage({ type: 'success', text: 'Rapport généré avec succès' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur génération rapport';
      setMessage({ type: 'error', text: msg });
    } finally {
      setGeneratingReport(false);
    }
  };

  // Export helpers
  const exportDocument = async (format, data, reportType) => {
    try {
      const res = await api.post(`/api/export/report/${format}`, {
        title: data.title || data.titre || 'Document IFRAP',
        content: data.synthesis || data.contenu || data.content || JSON.stringify(data),
        report_type: reportType,
      }, { responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'docx';
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `IFRAP_${reportType}_${new Date().toISOString().split('T')[0]}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Export ${format} error:`, err);
      alert(`Erreur lors de l'export ${format.toUpperCase()}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono text-[#00FF88] flex items-center gap-2">
            <Sparkles className="w-6 h-6" />
            Résumé IA
          </h1>
          <p className="text-gray-400 text-sm mt-1">Synthèses automatiques des articles de veille</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
          status?.configured 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {status?.configured ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {status?.configured ? 'LLM configuré' : 'LLM non configuré'}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Manual Scan */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <Play className="w-5 h-5" />
            Scan Manuel
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Catégories à scanner</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scanOptions.categories.includes('think_tanks')}
                    onChange={(e) => {
                      const cats = e.target.checked 
                        ? [...scanOptions.categories, 'think_tanks']
                        : scanOptions.categories.filter(c => c !== 'think_tanks');
                      setScanOptions({ ...scanOptions, categories: cats });
                    }}
                    className="w-4 h-4 accent-[#00FF88]"
                  />
                  <span className="text-gray-300">Think Tanks</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={scanOptions.categories.includes('media')}
                    onChange={(e) => {
                      const cats = e.target.checked 
                        ? [...scanOptions.categories, 'media']
                        : scanOptions.categories.filter(c => c !== 'media');
                      setScanOptions({ ...scanOptions, categories: cats });
                    }}
                    className="w-4 h-4 accent-[#00FF88]"
                  />
                  <span className="text-gray-300">Médias</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Nombre d'articles (max)</label>
              <input
                type="number"
                value={scanOptions.max_articles}
                onChange={(e) => setScanOptions({ ...scanOptions, max_articles: parseInt(e.target.value) || 20 })}
                min="5"
                max="50"
                className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white"
              />
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={scanOptions.force}
                onChange={(e) => setScanOptions({ ...scanOptions, force: e.target.checked })}
                className="w-4 h-4 accent-[#00FF88]"
              />
              <span className="text-gray-400 text-sm">Forcer le re-scan (ignorer le cache)</span>
            </label>

            <button
              onClick={handleScan}
              disabled={scanning || !status?.configured}
              className="w-full bg-[#00FF88] text-black font-medium py-3 rounded hover:bg-[#00FF88]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {scanning ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Scan en cours...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Lancer le scan
                </>
              )}
            </button>
          </div>
        </div>

        {/* Auto Scan Config */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Scan Automatique Quotidien
          </h2>

          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={autoConfig.enabled}
                onChange={(e) => setAutoConfig({ ...autoConfig, enabled: e.target.checked })}
                className="w-5 h-5 accent-[#00FF88]"
              />
              <span className="text-gray-300 font-medium">Activer le scan automatique</span>
            </label>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-400 mb-1">Heure</label>
                <select
                  value={autoConfig.hour}
                  onChange={(e) => setAutoConfig({ ...autoConfig, hour: parseInt(e.target.value) })}
                  className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}h</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-400 mb-1">Minutes</label>
                <select
                  value={autoConfig.minute}
                  onChange={(e) => setAutoConfig({ ...autoConfig, minute: parseInt(e.target.value) })}
                  className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white"
                >
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Nombre d'articles</label>
              <input
                type="number"
                value={autoConfig.max_articles}
                onChange={(e) => setAutoConfig({ ...autoConfig, max_articles: parseInt(e.target.value) || 30 })}
                min="10"
                max="100"
                className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white"
              />
            </div>

            <button
              onClick={handleSaveAutoConfig}
              className="w-full bg-blue-600 text-white font-medium py-2 rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Sauvegarder la configuration
            </button>

            {status?.auto_scan?.next_run && (
              <div className="flex items-center gap-2 text-sm text-gray-400 mt-2">
                <Clock className="w-4 h-4" />
                Prochain scan : {new Date(status.auto_scan.next_run).toLocaleString('fr-FR')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Last Scan Results */}
      {lastScan?.has_scan && (
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Dernier Scan
          </h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-[#00FF88]">{lastScan.articles_processed}</div>
              <div className="text-xs text-gray-500">Articles traités</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{lastScan.summaries_generated}</div>
              <div className="text-xs text-gray-500">Résumés générés</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-400">
                {lastScan.completed_at 
                  ? new Date(lastScan.completed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '-'}
              </div>
              <div className="text-xs text-gray-500">Heure du scan</div>
            </div>
          </div>

          {/* Digest */}
          {lastScan.digest && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Digest généré</h3>
              <div className="bg-black/30 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
                  {lastScan.digest}
                </pre>
              </div>
            </div>
          )}

          {/* Summaries List */}
          {lastScan.summaries && lastScan.summaries.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Résumés ({lastScan.summaries.length})</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {lastScan.summaries.map((s, i) => (
                  <div key={i} className="bg-black/30 rounded-lg p-4 border-l-2 border-[#00FF88]">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                        {s.source}
                      </span>
                    </div>
                    <h4 className="text-white font-medium text-sm mb-2">{s.title}</h4>
                    <p className="text-gray-400 text-sm">{s.ai_summary}</p>
                    {s.key_points && s.key_points.length > 0 && (
                      <ul className="mt-2 text-xs text-gray-500">
                        {s.key_points.map((point, j) => (
                          <li key={j} className="flex items-start gap-1">
                            <span className="text-[#00FF88]">•</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status Info */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Cache résumés</span>
          <span className="text-gray-300">{status?.cache_size || 0} articles en cache</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-gray-500">Historique scans</span>
          <span className="text-gray-300">{status?.scan_history_count || 0} scans enregistrés</span>
        </div>
      </div>

      {/* Report Generation Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Press Review */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6" data-testid="press-review-section">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Revue de Presse
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            Génère une synthèse des articles de veille récents avec analyse IFRAP.
          </p>
          <button
            onClick={handleGenerateReview}
            disabled={generatingReview}
            data-testid="generate-press-review-btn"
            className="w-full bg-[#00FF88] text-black font-medium py-3 rounded hover:bg-[#00FF88]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {generatingReview ? (
              <><RefreshCw className="w-5 h-5 animate-spin" /> Génération en cours...</>
            ) : (
              <><BookOpen className="w-5 h-5" /> Générer la Revue de Presse</>
            )}
          </button>

          {pressReview && (
            <div className="mt-4" data-testid="press-review-result">
              <div className="bg-black/30 rounded-lg p-4 max-h-64 overflow-y-auto mb-3">
                <h3 className="text-white font-medium mb-2">{pressReview.titre || pressReview.title}</h3>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">
                  {(pressReview.contenu || pressReview.synthesis || '').substring(0, 500)}...
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => exportDocument('pdf', pressReview, 'press_review')}
                  data-testid="export-review-pdf-btn"
                  className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" /> Export PDF
                </button>
                <button
                  onClick={() => exportDocument('word', pressReview, 'press_review')}
                  data-testid="export-review-word-btn"
                  className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" /> Export Word
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Report Generation */}
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6" data-testid="report-section">
          <h2 className="text-lg font-mono text-[#00FF88] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Rapport Thématique
          </h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Sujet du rapport</label>
              <input
                type="text"
                value={reportTopic}
                onChange={(e) => setReportTopic(e.target.value)}
                placeholder="Ex: Réforme des retraites, dette publique..."
                data-testid="report-topic-input"
                className="w-full bg-black/30 border border-gray-700 rounded px-3 py-2 text-white placeholder-gray-600"
              />
            </div>
            <button
              onClick={handleGenerateReport}
              disabled={generatingReport || !reportTopic.trim()}
              data-testid="generate-report-btn"
              className="w-full bg-[#00FF88] text-black font-medium py-3 rounded hover:bg-[#00FF88]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {generatingReport ? (
                <><RefreshCw className="w-5 h-5 animate-spin" /> Génération en cours...</>
              ) : (
                <><FileText className="w-5 h-5" /> Générer le Rapport</>
              )}
            </button>
          </div>

          {report && (
            <div className="mt-4" data-testid="report-result">
              <div className="bg-black/30 rounded-lg p-4 max-h-64 overflow-y-auto mb-3">
                <h3 className="text-white font-medium mb-2">{report.title || report.titre}</h3>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">
                  {(report.content || report.contenu || '').substring(0, 500)}...
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => exportDocument('pdf', report, 'report')}
                  data-testid="export-report-pdf-btn"
                  className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" /> Export PDF
                </button>
                <button
                  onClick={() => exportDocument('word', report, 'report')}
                  data-testid="export-report-word-btn"
                  className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" /> Export Word
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryPage;
